import { useCallback, useRef, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import { isValidBillingZipCode, normalizeZipCode } from '@src/domains/payments';
import { resolveAddress } from '@src/features/address';

import type { CardFormValues } from '../core/cardPaymentFormSchema';

const ADDRESS_LOOKUP_FIELDS = ['state', 'city', 'district', 'street'] as const;

type UseBillingAddressLookupInput = {
  enabled: boolean;
  isForeign: boolean;
  setValue: UseFormSetValue<CardFormValues>;
};

export const useBillingAddressLookup = ({ enabled, isForeign, setValue }: UseBillingAddressLookupInput) => {
  const [lookingUpAddress, setLookingUpAddress] = useState(false);
  const lastLookedUpZipCode = useRef<string | null>(null);
  const lookupRequestId = useRef(0);

  const lookUpAddress = useCallback(
    async (rawZipCode: string) => {
      const zipCode = normalizeZipCode(rawZipCode);

      if (!enabled || isForeign || !zipCode || !isValidBillingZipCode(zipCode, isForeign)) {
        return;
      }

      if (zipCode === lastLookedUpZipCode.current) {
        return;
      }

      lastLookedUpZipCode.current = zipCode;
      lookupRequestId.current += 1;
      const requestId = lookupRequestId.current;
      setLookingUpAddress(true);

      try {
        const address = await resolveAddress(zipCode, isForeign);

        if (requestId !== lookupRequestId.current) {
          return;
        }

        const hasAnyField = ADDRESS_LOOKUP_FIELDS.some((field) => address[field]);

        if (!hasAnyField) {
          lastLookedUpZipCode.current = null;
          return;
        }

        const written = { shouldValidate: true, shouldDirty: true };

        ADDRESS_LOOKUP_FIELDS.forEach((field) => {
          setValue(`billingAddress.${field}`, address[field] ?? '', written);
        });
      } catch {
        if (requestId !== lookupRequestId.current) {
          return;
        }

        lastLookedUpZipCode.current = null;
      } finally {
        if (requestId === lookupRequestId.current) {
          setLookingUpAddress(false);
        }
      }
    },
    [enabled, isForeign, setValue],
  );

  return { lookingUpAddress, lookUpAddress };
};

export default useBillingAddressLookup;
